/**
 * Pure helpers behind the Skills admin screen.
 *
 * Same reasoning as `settings.js`: no Vue, no axios, no component state, so the rules
 * that are easy to get quietly wrong — which version may be edited, which may be
 * published, whether a Skill can actually be offered to anyone, and how much of the
 * context window its instructions are about to eat — are testable on their own.
 * `skills.spec.js` covers them.
 */

/** The backend's own bounds. Checked here so a typo is a sentence, not a 400. */
export const MAX_SLUG_CHARS = 64;
export const MAX_NAME_CHARS = 128;
export const MAX_DESCRIPTION_CHARS = 512;

/**
 * The hard character cap the API enforces.
 *
 * It is not the real limit and the interface must not present it as one — see
 * `describeBodyBudget`. This exists to stop a pathological paste, nothing more.
 */
export const MAX_BODY_CHARS = 100000;

/** `^[a-z0-9]+(-[a-z0-9]+)*$` — lowercase kebab-case, as the server validates it. */
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * The rough chars-per-token ratio for English prose.
 *
 * Deliberately an estimate and labelled as one wherever it is shown. The exact count is
 * the model's tokeniser's business and depends on the text; four is the standard
 * approximation and is close enough to answer the only question being asked here — is
 * this instruction the size of a paragraph or the size of a chapter.
 */
export const CHARS_PER_TOKEN = 4;

/**
 * Where the warning turns from "this is getting long" to "this is crowding out the
 * conversation".
 *
 * A Skill's body is charged against `max_context_tokens` before a single turn of
 * conversation is added, so at half the window the assistant has less room for the
 * exchange than for its own instructions — which is the point where a long thread starts
 * being truncated for reasons the author never connected to what they wrote.
 */
export const CROWDING_SHARE = 0.25;
export const OVERSIZED_SHARE = 0.5;

/**
 * Whether a slug is one the server will accept, and if not, why.
 *
 * @param {String} slug
 * @returns {String|null} the sentence to show, or null when it is fine
 */
export function validateSlug(slug) {
  const value = (slug || "").trim();

  if (!value) {
    return "A slug is required.";
  }
  if (value.length > MAX_SLUG_CHARS) {
    return `${MAX_SLUG_CHARS} characters at most.`;
  }
  if (!SLUG_PATTERN.test(value)) {
    return "Lowercase letters, numbers and single hyphens only — like grill-me.";
  }

  return null;
}

/**
 * A slug proposed from the name.
 *
 * Only ever a suggestion, and only while the author has not typed a slug of their own:
 * the slug is permanent-ish in the way a name is not, and silently rewriting one someone
 * chose would be worse than making them type it.
 *
 * @param {String} name
 * @returns {String}
 */
export function slugify(name) {
  return (name || "")
    .toLowerCase()
    .normalize("NFKD")
    // Anything that is not a letter, a digit or a hyphen becomes a boundary, so
    // "Grill Me (v2)" gives "grill-me-v2" rather than dropping the punctuation and
    // gluing the words together.
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_CHARS)
    // A trailing hyphen can reappear after the slice.
    .replace(/-+$/g, "");
}

/**
 * What the instructions cost, measured against the window they have to share.
 *
 * The character cap is 100,000 and the real bound is `ai_config.max_context_tokens`,
 * typically 16,000 — which the body is charged against *before* any conversation. An
 * interface that shows "0 / 100,000" is telling the author something true and useless,
 * and the failure it invites lands much later, in someone else's chat, for a reason
 * nobody connects back to this textarea.
 *
 * @param {String} body
 * @param {Number} maxContextTokens - from the AI config; null when not loaded yet
 * @returns {Object} { chars, estimatedTokens, share, level, message }
 *   level is one of "empty" | "ok" | "crowding" | "oversized" | "over-cap"
 */
export function describeBodyBudget(body, maxContextTokens) {
  const chars = (body || "").length;
  const estimatedTokens = Math.ceil(chars / CHARS_PER_TOKEN);

  if (chars > MAX_BODY_CHARS) {
    return {
      chars,
      estimatedTokens,
      share: null,
      level: "over-cap",
      message: `Too long to save — ${MAX_BODY_CHARS.toLocaleString()} characters at most.`,
    };
  }

  if (!chars) {
    return {
      chars,
      estimatedTokens,
      share: null,
      level: "empty",
      message: "Instructions are required.",
    };
  }

  // Without the config there is nothing to measure against, so say the size and stop
  // short of a judgement. A warning threshold invented from a default nobody set is
  // how an author learns to ignore the warning.
  if (!maxContextTokens) {
    return { chars, estimatedTokens, share: null, level: "ok", message: null };
  }

  const share = estimatedTokens / maxContextTokens;

  if (share >= OVERSIZED_SHARE) {
    return {
      chars,
      estimatedTokens,
      share,
      level: "oversized",
      message:
        `These instructions are about ${percent(share)} of the ${formatCount(maxContextTokens)}-token ` +
        "context window on their own, before any of the conversation. Long threads will be " +
        "cut short. A few thousand characters is the range that works.",
    };
  }

  if (share >= CROWDING_SHARE) {
    return {
      chars,
      estimatedTokens,
      share,
      level: "crowding",
      message:
        `About ${percent(share)} of the context window goes to these instructions before ` +
        "the conversation starts. Still workable, but shorter leaves more room for the thread.",
    };
  }

  return { chars, estimatedTokens, share, level: "ok", message: null };
}

/**
 * Whether this Skill can actually be offered to anyone, and what to say when it cannot.
 *
 * `publishedVersion: null` is the state worth surfacing loudly. It is the difference
 * between a Skill that is merely unfinished and one that is broken, and neither the
 * status pill nor the version count says which — a Skill with three drafts and nothing
 * published looks busy and serves nobody.
 *
 * @param {Object} skill - a SkillView
 * @returns {Object} { offered, level, message }
 */
export function describeSkillHealth(skill) {
  if (!skill) {
    return { offered: false, level: "unknown", message: null };
  }

  if (skill.status === "archived") {
    return {
      offered: false,
      level: "retired",
      // Not a fault. Archiving is how a Skill is retired, and the conversations that
      // used it stay readable — which is the whole reason the row survives.
      message: "Retired. It is not offered to anyone; conversations that used it stay readable.",
    };
  }

  if (!skill.publishedVersion) {
    return {
      offered: false,
      level: "broken",
      message:
        "No published version, so this Skill cannot be offered to anyone. Publish a draft " +
        "to make it live.",
    };
  }

  if (skill.status !== "published") {
    return {
      offered: false,
      level: "unlisted",
      message: "Not offered in the picker. Set its status to published to list it.",
    };
  }

  return { offered: true, level: "live", message: null };
}

/**
 * What may be done to one version, given what it is and what the user holds.
 *
 * The one rule the interface has to get structurally right: a published version is never
 * edited. Improving a Skill is create-a-draft-then-publish, and the backend refuses the
 * edit outright — so the control is absent, not disabled. A disabled control says "not
 * now"; absence says "that is not how this works", which is the truth.
 *
 * @param {Object} version - a SkillVersionView
 * @param {Boolean} canPublish - whether the user holds `ai.skill.publish`
 * @returns {Object} { isDraft, canEdit, canPublish, canCopyToDraft }
 */
export function describeVersionActions(version, canPublish) {
  const isDraft = Boolean(version) && version.status === "draft";

  return {
    isDraft,
    canEdit: isDraft,
    canPublish: isDraft && Boolean(canPublish),
    // Every version can seed a new draft, including the live one — that is how a small
    // correction to published text is made, and how a rollback is expressed.
    canCopyToDraft: Boolean(version),
  };
}

/**
 * The draft a Skill is currently being written in, if there is one.
 *
 * At most one matters: versions come back newest first, and the newest draft is the one
 * the author is working on.
 *
 * @param {Object} skill - a SkillDetailView
 * @returns {Object|null}
 */
export function currentDraft(skill) {
  const versions = (skill && skill.versions) || [];
  return versions.find((version) => version.status === "draft") || null;
}

/**
 * Versions whose stored text no longer matches the hash taken when it was published.
 *
 * Something wrote to the row outside the application. The same check runs at send time
 * and fails the user's message with `E1018-023`; reporting it here moves the discovery
 * from *a user's chat broke* to *a warning on an admin screen*, which is the entire
 * point of returning the flag.
 *
 * @param {Object} skill - a SkillDetailView
 * @returns {Array} the affected versions
 */
export function tamperedVersions(skill) {
  const versions = (skill && skill.versions) || [];
  return versions.filter((version) => version.bodyIntact === false);
}

/** "12%" — shares are only ever shown as a rough proportion, never a decimal. */
function percent(share) {
  return `${Math.round(share * 100)}%`;
}

function formatCount(value) {
  return Number(value).toLocaleString();
}
